import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AccountPartnershipResult, type ApiError } from '@whinerp/client';
import { formatDate, formatDateTime } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useFormat, useT, useErrorMessage } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Whether this company is a partner, and the links its people use (F-061).
 *
 * On the company, because that is where somebody is when they decide a company
 * is one - and it needs no picker to find the company. The link is shown once,
 * after it is issued: the product keeps no readable copy, so there is nothing
 * to show later.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

export function AccountPartnerPanel({ accountId }: { accountId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const entitlement = useEntitlement('F-061');
  const manage = can('partner.manage');
  const [days, setDays] = useState('');
  const [link, setLink] = useState<{ contactId: string; url: string; expiresAt: string } | null>(null);

  const partnership = useQuery<AccountPartnershipResult, ApiError>({
    queryKey: ['account-partner', accountId],
    queryFn: () => api.accountPartnership({ path: { accountId } }),
    enabled: entitlement.enabled,
  });

  useEffect(() => {
    if (partnership.data) setDays(String(partnership.data.protectionDays));
  }, [partnership.data]);

  const save = useMutation<unknown, ApiError, { partner: boolean; protectionDays?: number }>({
    mutationFn: (body) => api.setPartner({ path: { accountId }, body }),
    onSuccess: async () => {
      setLink(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['account-partner', accountId] }),
        queryClient.invalidateQueries({ queryKey: ['partners'] }),
      ]);
    },
  });

  const issue = useMutation<{ path: string; expiresAt: string }, ApiError, string>({
    mutationFn: (contactId) => api.issuePartnerLink({ path: { contactId } }),
    onSuccess: (result, contactId) =>
      setLink({ contactId, url: `${window.location.origin}${result.path}`, expiresAt: result.expiresAt }),
  });

  if (!entitlement.enabled) return null;
  if (partnership.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (partnership.isError) {
    return (
      <Panel data-region="account-partner">
        <p data-account-partner-error className="text-dense text-danger-text">
          {errorText(partnership.error)}
        </p>
      </Panel>
    );
  }

  const current = partnership.data;
  // Only for somebody who can change it. A company that is not a partner says
  // nothing to a rep, who has no reason to be told what it is not.
  if (!current.partner && !manage) return null;

  const refusal = save.error ?? issue.error;
  const typedDays = Number(days);

  return (
    <Panel data-region="account-partner" data-partner={current.partner ? 'yes' : 'no'}>
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('partner.accountPanel')}</SectionLabel>

        {refusal ? (
          <p data-account-partner-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {errorText(refusal)}
          </p>
        ) : null}

        {current.partner ? (
          <p className="text-body text-ink">
            {t('partner.since', { values: { date: formatDate(current.partnerSince ?? '', format) } })}
          </p>
        ) : (
          <p className="text-meta text-ink-meta">{t('partner.notPartner')}</p>
        )}

        {manage ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('partner.protectionLabel')}
              <input
                data-partner-days
                type="number"
                min={1}
                max={730}
                className={`${INPUT} w-col-count`}
                value={days}
                onChange={(event) => setDays(event.target.value)}
              />
            </label>
            {current.partner ? (
              <>
                <Button
                  intent="secondary"
                  size="small"
                  data-partner-save-days
                  disabled={save.isPending}
                  onClick={() => save.mutate({ partner: true, protectionDays: typedDays })}
                >
                  {t('partner.saveDays')}
                </Button>
                <Button
                  intent="danger"
                  size="small"
                  data-partner-end
                  disabled={save.isPending}
                  onClick={() => save.mutate({ partner: false })}
                >
                  {t('partner.end')}
                </Button>
              </>
            ) : (
              <Button
                intent="primary"
                size="small"
                data-partner-make
                disabled={save.isPending}
                onClick={() => save.mutate({ partner: true, protectionDays: typedDays })}
              >
                {t('partner.make')}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-meta text-ink-meta">
            {t('partner.protectionDays', { values: { days: String(current.protectionDays) } })}
          </p>
        )}

        {current.partner && manage ? (
          current.contacts.length === 0 ? (
            <p data-partner-no-people className="text-meta text-ink-meta">
              {t('partner.noPeople')}
            </p>
          ) : (
            <ul className="flex flex-col">
              {current.contacts.map((contact) => (
                <li
                  key={contact.contactId}
                  data-partner-person={contact.contactId}
                  className="flex flex-col gap-1 border-t border-t-rule py-1 first:border-t-0"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-body text-ink">{contact.name}</span>
                    <Button
                      intent="ghost"
                      size="small"
                      data-partner-link={contact.contactId}
                      disabled={issue.isPending}
                      onClick={() => issue.mutate(contact.contactId)}
                    >
                      {t('partner.issueLink')}
                    </Button>
                  </div>
                  {link?.contactId === contact.contactId ? (
                    <label className="flex flex-col gap-1 text-meta text-ink-meta">
                      {t('partner.linkIssued', { values: { date: formatDateTime(link.expiresAt, format) } })}
                      <input data-partner-link-url readOnly className={INPUT} value={link.url} />
                    </label>
                  ) : null}
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>
    </Panel>
  );
}
